import _ from 'underscore';
import systemNachrichtService from './systemNachrichtService.js';
import stammdatenService from './stammdatenService.js';
import dayjs from '../../../../shared/services/dayjs.js';
import SystemDialogService from '../../../../shared/services/systemDialogService.js';
import { zeigeFehlerFenster } from '../util.js';

class SchichtService {
    // bildID -> { MimeType, Bild (base64), Typ, Aufnahmedatum }; vermeidet erneutes Laden bei wiederholtem Hover.
    kameraBildCache = new Map();

    // Speicher Request an den Server für eine Schicht
    async speichereSchicht(schichtObjekt, pausenmodellVerwenden, ignoreSucessMsg = false) {
        try {
            // wenn wir eine neue Schicht erstellen, ist eine POST Request
            const istNeu = _.isEmpty(schichtObjekt._id);
            const params = new URLSearchParams({
                PausenmodellVerwenden: pausenmodellVerwenden
            });
            const response = await fetch(`/neolohn/api/zeiten/schichten?${params}`, {
                method: istNeu ? 'POST' : 'PUT',
                mode: 'cors',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(schichtObjekt),
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            ignoreSucessMsg ? '' : systemNachrichtService.zeigeKleineNachricht('Erfolgreich gespeichert!', 1);
            await stammdatenService.holeMitarbeiter(stammdatenService.aktuellerMitarbeiter._id);
            return result;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Speichern!', -1);
            return false;
        }
    }

    // Liest eine Schicht aus
    async leseSchicht(schichtID) {
        try {
            const response = await fetch(`/neolohn/api/zeiten/schichten/${schichtID}`);
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            return result;
        } catch (error) {
            console.error(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Lesen der Schicht!', -1);
            return false;
        }
    }

    // Lese Request für alle Schichten des ausgewählten Mitarbeiters eines Zeitraums
    async leseAlleSchichtenMitarbeiter(datumVon, datumBis, mitarbeiterID) {
        // standardmäßig geben wir den aktuellen Monat an
        if (!datumVon && !datumBis) {
            datumVon = dayjs(window.myVars.aktuellesMonatsDatum).startOf('month').format('YYYY-MM-DD');
            datumBis = dayjs(window.myVars.aktuellesMonatsDatum).endOf('month').format('YYYY-MM-DD');
        }
        try {
            const response = await fetch(`/neolohn/api/zeiten/mitarbeiter/${mitarbeiterID}?StartDatum=${datumVon}&EndDatum=${datumBis}`);
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            return result;
        } catch (error) {
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Laden der Schichten!', -1);
            console.error(error);
            return false;
        }
    }

    // Lösch Request für eine bestimmte Schicht
    async zeitLoeschen(schichtID) {
        try {
            const response = await fetch(`/neolohn/api/zeiten/schichten/${schichtID}`, {
                method: 'DELETE',
                mode: 'cors',
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Erfolgreich gelöscht!', 1);
            // wir holen die Mitarbeiter daten neu, für aktuelle Statuswerte nach Löschen einer Schicht...
            await stammdatenService.holeMitarbeiter(stammdatenService.aktuellerMitarbeiter._id);
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Löschen!', -1);
            return false;
        }
    }

    // Bestätigt eine Zeiterfassungs-Schicht (Mitarbeiter > Zeiten / Tagesübersicht)
    async bestaetigeSchicht(schichtID) {
        try {
            const response = await fetch(`/neolohn/api/zeiten/schichten/${schichtID}/bestaetigen`, {
                method: 'PUT',
                mode: 'cors',
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Schicht bestätigt!', 1);
            return await response.json();
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Bestätigen!', -1);
            return false;
        }
    }

    /**
     * Lädt ein einzelnes Kamerabild (Zeiterfassung Start/Ende), gecached nach bildID.
     * @param {string} bildID
     * @returns {object|null} { MimeType, Bild (base64), Typ, Aufnahmedatum }
     */
    async leseKamerabild(bildID) {
        if (this.kameraBildCache.has(bildID)) return this.kameraBildCache.get(bildID);
        try {
            const response = await fetch(`/neolohn/api/tagesuebersicht/kamera/${bildID}`);
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            this.kameraBildCache.set(bildID, result);
            return result;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    /**
     * Erstellt einen genehmigten Urlaubsantrag und relevante Urlaubsschichten.
     * @param {string} mitarbeiterID objectId
     * @param {Date} urlaubVon
     * @param {Date} urlaubBis
     * @returns true (success) oder false (error)
     */
    async erstelleUrlaubsschichten(mitarbeiterID, urlaubVon, urlaubBis) {
        try {
            document.body.style.cursor = 'wait';
            const body = {
                MitarbeiterID: mitarbeiterID,
                UrlaubVon: urlaubVon,
                UrlaubBis: urlaubBis
            };
            const response = await fetch(`/neolohn/api/zeiten/mitarbeiter/${mitarbeiterID}/urlaub`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Urlaub Erfolgreich gespeichert!', 1);
            document.body.style.cursor = 'auto';
            // wir holen die Mitarbeiter daten neu, für aktuelle Statuswerte nach Urlaubseintragung.
            await stammdatenService.holeMitarbeiter(mitarbeiterID);
            return true;            
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler bei der Urlaubsanfrage!', -1);
            document.body.style.cursor = 'auto';
            return false;
        }
    }

    /**
     * Erstellt eine entsprechende Anzahl an EGFZ Schichten
     * @param {*} mitarbeiterInfo relevante Informationen für die EGFZ Schichten
     * - MitarbeiterID
     * - DatumVon
     * - DatumBis
     * - EgfzID
     * - QualifikationID
     * - PostenID
     * @returns true (success) oder false (error)
     */
    async erstelleEgfzTage(mitarbeiterInfo) {
         try {
            document.body.style.cursor = 'wait';
            const body = mitarbeiterInfo;
            const response = await fetch(`/neolohn/api/zeiten/mitarbeiter/${mitarbeiterInfo.MitarbeiterID}/egfz`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('EGFZ Tage erfolgreich eingetragen!', 1);
            document.body.style.cursor = 'auto';
            // wir holen die Mitarbeiter daten neu, für aktuelle Statuswerte nach EGFZ Eintragung.
            await stammdatenService.holeMitarbeiter(mitarbeiterInfo.MitarbeiterID);
            return true;            
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler bei der Anfrage!', -1);
            document.body.style.cursor = 'auto';
            return false;
        }
    }

    /**
     * Stellt eine DELETE Request an den Server für die Widerrufung der Urlaubsschichten aus dem Antrag
     * @param {ObjectIdString} mitarbeiterID
     * @param {ObjectIdString} urlaubID
     * @param {String} statusUpdate
     * @returns Urlaubsantrag
     */
    async entferneUrlaub(mitarbeiterID, urlaubID, statusUpdate) {
        try {
            const body = {
                MitarbeiterID: mitarbeiterID,
                UrlaubID: urlaubID,
                StatusUpdate: statusUpdate
            };
            const response = await fetch('/neolohn/api/mitarbeiter/urlaub', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const message = await response.text();
                systemNachrichtService.zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Urlaubsantrag erfolgreich widerrufen!', 1);
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler bei der Urlaubswiderrufung!', -1);
            return false;
        }
    }

    /**
     * Stellt eine POST Request an den Server für die Erstellung der Urlaubsschichten aus dem Antrag
     * @param {ObjectIdString} mitarbeiterID
     * @param {ObjectIdString} urlaubID
     * @param {String} statusUpdate
     */
    async erstelleUrlaubAusAntrag(mitarbeiterID, urlaubID, statusUpdate) {
        try {
            const body = {
                MitarbeiterID: mitarbeiterID,
                UrlaubID: urlaubID,
                StatusUpdate: statusUpdate
            };
            const response = await fetch('/neolohn/api/mitarbeiter/urlaub', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Urlaubsantrag erfolgreich genehmigt!', 1);
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler bei der Urlaubserstellung!', -1);
            return false;
        }
    }

    /**
     * Importiert Mitarbeiter Zeiten aus E2N
     * @param {string} zeitraum Zeitraum Monat der importiert werden soll...
     * @param {string} betriebID für welchen Betrieb importiert werden soll
     */
    async importiereZeitenE2N(zeitraum, betriebID) {
        try {
            window.myHandlers.toggleLoader();
            const fetchUrl = '/neolohn/api/zeiten/import/e2n?';
            const params = new URLSearchParams();
            params.append('Zeitraum', zeitraum);
            params.append('BetriebsstaetteID', betriebID);
            const response = await fetch(`${fetchUrl}${params.toString()}`);
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            systemNachrichtService.zeigeKleineNachricht('Zeitenimport erfolgreich!', 1);
            window.myHandlers.toggleLoader();
            // Falls es eine Fehlerliste gibt...
            if (result.length >= 1) {
                result.sort((a, b) => a.personalnummer.localeCompare(b.personalnummer));
                zeigeFehlerFenster('E2N Import Hinweise', {
                    beschreibung: 'Folgende Abwesenheiten konnten nicht mit EGFZ zugeordnet werden:',
                    tabelle: {
                        headers: ['Datum', 'Personalnummer', 'AbwesenheitsTyp/EGFZ', 'Dauer in Minuten'],
                        rows: result.map((fehler) => [fehler.datum, fehler.personalnummer, fehler.abwesenheitsTyp, fehler.dauer])
                    }
                });
            }
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Zeitenimport fehlgeschalgen!', -1);
            window.myHandlers.toggleLoader();
        }
    }
    /**
     * Importiert Mitarbeiter Zeiten aus Eigener API
     * @param {string} zeitraum Zeitraum Monat der importiert werden soll...
     * @param {string} betriebID für welchen Betrieb importiert werden soll
     */
    async importiereZeitenCustomAPI(zeitraum, betriebID, pausenmodell) {
        try {
            window.myHandlers.toggleLoader();
            const fetchUrl = '/neolohn/api/zeiten/import/custom-api?';
            const params = new URLSearchParams();
            params.append('Zeitraum', zeitraum);
            params.append('BetriebsstaetteID', betriebID);
            params.append('Pausenmodell', pausenmodell);
            const response = await fetch(`${fetchUrl}${params.toString()}`);
            if (!response.ok) {
                const msg = await response.text();
                // Sowohl Schema-Validierungsfehler als auch nicht gefundene Personalnummern kommen als JSON Liste zurück
                let fehlerListe;
                try {
                    fehlerListe = JSON.parse(msg);
                    if (!Array.isArray(fehlerListe)) fehlerListe = undefined;
                } catch {
                    fehlerListe = undefined;
                }
                if (fehlerListe) {
                    zeigeFehlerFenster('API Zeitenimport Fehler', {
                        beschreibung: 'Folgende Validierungsfehler sind aufgetreten:',
                        liste: fehlerListe,
                        hinweis: 'Bitte überprüfen Sie, ob die API die neolohn Vorgaben unterstützt.'
                    });
                } else {
                    systemNachrichtService.zeigeKleineNachricht(msg);
                }
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            systemNachrichtService.zeigeKleineNachricht(`${result.anzahlSchichten} Schichten für ${result.anzahlMitarbeiter} Mitarbeiter importiert!`, 1);
            window.myHandlers.toggleLoader();
            // Nicht gefundene Personalnummern werden importiert, aber trotzdem als Hinweis angezeigt
            if (result.notFoundPNRS?.length >= 1) {
                zeigeFehlerFenster('API Zeitenimport Hinweise', {
                    beschreibung: 'Folgende Personalnummern konnten nicht zugeordnet werden und wurden übersprungen:',
                    liste: result.notFoundPNRS,
                    hinweis: 'Bitte überprüfen Sie, ob alle Mitarbeiter Personalnummern in neolohn vorhanden sind.'
                });
            }
            return result;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler! Bitte überprüfen Sie Ihre API Angaben!', -1);
            window.myHandlers.toggleLoader();
        }
    }

    /**
     * Sendet eine Serveranfrage für die monatliche Gutzeitautomatisierung...
     * @param {string} mitarbeiterID 
     * @param {string} zeitraum 
     * @returns 
     */
    async erstelleMonatlicheGutzeitMA(mitarbeiterID, zeitraum) {
        try {
            window.myHandlers.toggleLoader();
            const response = await fetch('/neolohn/api/zeiten/schichten/gutzeit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Zeitraum: zeitraum,
                    MitarbeiterID: mitarbeiterID
                })
            });
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Gutzeit erfolgreich eingetragen!', 1);
            window.myHandlers.toggleLoader();
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Anfrage fehlgeschalgen!', -1);
            window.myHandlers.toggleLoader();
            return false;
        }
    }

    async kopiereArbeitszeitenVormonat(mitarbeiterID, zeitraum) {
        try {
            window.myHandlers.toggleLoader();
            const response = await fetch('/neolohn/api/zeiten/schichten/vormonat-kopieren', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Zeitraum: zeitraum, // aktueller Monat
                    MitarbeiterID: mitarbeiterID
                })
            });
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Arbeitszeiten erfolgreich kopiert!', 1);
            window.myHandlers.toggleLoader();
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Anfrage fehlgeschalgen!', -1);
            window.myHandlers.toggleLoader();
            return false;
        }
    
    }

    async erstelleRegelarbeitszeitenMonat(mitarbeiterID, zeitraum) {
        try {
            window.myHandlers.toggleLoader();
            const response = await fetch('/neolohn/api/zeiten/schichten/regelarbeitzeiten-monat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Zeitraum: zeitraum, // aktueller Monat
                    MitarbeiterID: mitarbeiterID
                })
            });
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            systemNachrichtService.zeigeKleineNachricht('Arbeitszeiten erfolgreich eingefügt!', 1);
            window.myHandlers.toggleLoader();
            return true;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Anfrage fehlgeschalgen!', -1);
            window.myHandlers.toggleLoader();
            return false;
        }
    
    }

    async leseAlleSchichtenMonatsuebersicht(zeitraum, betriebsstaetteID) {
        try {
            const urlParams = new URLSearchParams({ Zeitraum: zeitraum, BetriebsstaetteID: betriebsstaetteID });
            const response = await fetch(`/neolohn/api/zeiten/monat?${urlParams.toString()}`);
            if (!response.ok) {
                const msg = await response.text();
                systemNachrichtService.zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            return result;
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Laden der Zeiten!', -1);
            return [];
        }
    }

    async importiereZeitenCSV(csvFile, pausenmodell) {
        try {
            window.myHandlers.toggleLoader();
            const response = await fetch(`/neolohn/api/zeiten/import/csv?Pausenmodell=${pausenmodell}`,
                {
                    method: 'POST',
                    body: csvFile
                }
            );
            if (!response.ok) {
                const msg = await response.text();
                // Bei einer ValidationException (CSV Validierungsfehler) kommt eine JSON Liste zurück
                let validierungsFehler;
                try {
                    validierungsFehler = JSON.parse(msg);
                    if (!Array.isArray(validierungsFehler)) validierungsFehler = undefined;
                } catch {
                    validierungsFehler = undefined;
                }
                if (validierungsFehler) {
                    zeigeFehlerFenster('CSV Zeitenimport Validierungsfehler', {
                        beschreibung: 'Folgende Fehler wurden in der CSV Datei gefunden:',
                        liste: validierungsFehler
                    });
                } else {
                    systemNachrichtService.zeigeKleineNachricht(msg);
                    // Bei NotFoundException die Liste an MA zeigen
                    if (response.status == '404') {
                        const split = msg.split(':');
                        const pnrs = split[1] ? split[1].trim().split(',').map((pnr) => pnr.trim()) : [];
                        zeigeFehlerFenster('CSV Zeitenimport Fehler', {
                            beschreibung: 'Folgende Personalnummern konnten nicht zugeordnet werden:',
                            liste: pnrs,
                            hinweis: 'Bitte überprüfen Sie, ob alle Mitarbeiter Personalnummern in neolohn vorhanden sind.'
                        });
                    }
                }
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            const result = await response.json();
            systemNachrichtService.zeigeKleineNachricht('Zeitenimport erfolgreich!',  1);
            window.myHandlers.toggleLoader();
            // Falls es eine Fehlerliste gibt...
            if (result.uniqueMismatches.length >= 1) {
                result.uniqueMismatches.sort((a, b) => a.pnr.localeCompare(b.pnr));
                zeigeFehlerFenster('CSV Import Hinweise', {
                    beschreibung: 'Folgende Mitarbeiter-Qualifikationen konnten nicht passend zugeordnet werden:',
                    tabelle: {
                        headers: ['Personalnummer', 'Qualifikationsnr. aus CSV', 'Grund'],
                        rows: result.uniqueMismatches.map((fehler) => [fehler.pnr, fehler.qnr, fehler.reason])
                    },
                    hinweis: 'Bitte überprüfen Sie die Mitarbeiter Qualifikationen in den importierten Zeiten.'
                });
            }
            if (result.anzahlMitarbeiter && result.anzahlSchichten) {
                document.getElementById('u-csv-zeiten-import-erfolg-ma').innerText = result.anzahlMitarbeiter;
                document.getElementById('u-csv-zeiten-import-erfolg-schichten').innerText = result.anzahlSchichten;
                if (result.uniqueMismatches.length >= 1) {
                    document.getElementById('u-csv-zeiten-import-erfolg-qualis').style.display = 'block';
                }
                SystemDialogService.instance.display('u-csv-zeiten-import-erfolg');                
            }
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Zeitenimport!', -1);
            window.myHandlers.toggleLoader();
        }
    }
}

const schichtService = new SchichtService();
export default schichtService;
